import { useEffect, useState } from 'react';
import whatsappService from '../services/wa-personal/whatsappService';
import waBusinessService from '../services/wa-business/waBusinessService';
import instagramService from '../services/instagram/instagramService';
import messengerService from '../services/messenger/messengerService';
import widgetService from '../services/widget/widgetService';

/**
 * Detecta quais canais (QR / Business / Instagram) o usuario tem ativos,
 * usando os endpoints que o Inbox e o Onboarding ja consomem em producao:
 *   - GET /api/whatsapp/status        (whatsappService.getConnectionStatus)
 *   - GET /api/wa-business/status     (waBusinessService.getStatus)
 *   - GET /api/instagram/status       (instagramService.getStatus — 404
 *     quando a flag do canal esta desligada; o catch vira "nao conectado")
 *
 * Sem endpoint novo — reusamos exatamente o que ja funciona, mantendo o
 * padrao arquitetural da plataforma. Chamadas em paralelo com catch
 * individual: se o usuario nao tiver instance/account, o canal volta
 * como nao conectado em vez de quebrar o modal.
 *
 * Retorna { loading, qr, business, instagram, both, channelFilter }.
 * `channelFilter` segue SO-WhatsApp de proposito: e o valor passado direto
 * ao broadcast por etiqueta (label_dispatch_routes.channel_filter), cujo
 * dominio e 'wa_personal'|'wa_business'|'both'. O Instagram entra pelos
 * fluxos que leem o campo `instagram` explicitamente (ex.: wizard de
 * campanha).
 */
export default function useActiveConnections({ enabled = true, withInstagram = false, withMessenger = false, withWidget = false } = {}) {
  const [loading, setLoading] = useState(enabled);
  const [qr, setQr] = useState(false);
  const [business, setBusiness] = useState(false);
  const [instagram, setInstagram] = useState(false);
  const [messenger, setMessenger] = useState(false);
  const [widget, setWidget] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    const qrPromise = whatsappService.getConnectionStatus()
      .then((res) => {
        const data = res?.data || res || {};
        return !!data.connected;
      })
      .catch(() => false);

    const businessPromise = waBusinessService.getStatus()
      .then((res) => {
        const data = res?.data || res || {};
        return !!data.connected;
      })
      .catch(() => false);

    // Opt-in: o status do Instagram só é consultado por quem declara usar o
    // canal (wizard de campanha). Consumidores só-WhatsApp (broadcast de
    // etiqueta, agendar mensagem) seguem com as 2 chamadas de sempre.
    const instagramPromise = withInstagram
      ? instagramService.getStatus()
        .then((res) => {
          const data = res?.data || res || {};
          return !!data.connected;
        })
        .catch(() => false)
      : Promise.resolve(false);

    // Mesmo opt-in do Instagram: só o wizard de campanha (e afins) consulta.
    const messengerPromise = withMessenger
      ? messengerService.getStatus()
        .then((res) => {
          const data = res?.data || res || {};
          return !!data.connected;
        })
        .catch(() => false)
      : Promise.resolve(false);

    // 5o canal (widget do site), mesmo opt-in dos canais da Meta.
    const widgetPromise = withWidget
      ? widgetService.getStatus()
        .then((res) => {
          const data = res?.data || res || {};
          return !!data.connected;
        })
        .catch(() => false)
      : Promise.resolve(false);

    Promise.all([qrPromise, businessPromise, instagramPromise, messengerPromise, widgetPromise])
      .then(([qrConnected, businessConnected, igConnected, msConnected, wdConnected]) => {
        if (cancelled) return;
        setQr(qrConnected);
        setBusiness(businessConnected);
        setInstagram(igConnected);
        setMessenger(msConnected);
        setWidget(wdConnected);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [enabled, withInstagram, withMessenger, withWidget]);

  const both = qr && business;
  let channelFilter = null;
  if (!loading) {
    if (both) channelFilter = 'both';
    else if (qr) channelFilter = 'wa_personal';
    else if (business) channelFilter = 'wa_business';
    else channelFilter = 'both'; // sem nada conectado: deixa backend decidir
  }

  return { loading, qr, business, instagram, messenger, widget, both, channelFilter };
}
